<template>
    <BlockContainer
        :id="'blog-article-grid-' + slugify(data.heading ?? '')"
        :background-color="data.backgroundColor"
        :bottom-angle="data.bottomAngle"
        :angle-background-color="data.nextBackgroundColor"
    >
        <div class="py-8 md:py-20">
            <h2 v-if="data.heading" class="mb-10">
                {{ data.heading }}
            </h2>
            <div
                v-if="data.featuredArticle"
                class="mb-8 gap-x-8 md:flex md:items-stretch"
            >
                <BaseCard
                    :card="blogCard(data.featuredArticle)"
                    :main-classes="
                        [
                            data.showCta
                                ? 'md:w-2/3 lg:grow lg:!h-auto mb-8 md:mb-0 !no-underline'
                                : '',
                        ].join('')
                    "
                    :image-container-classes="'bg-gray-200 rounded-bl-[10px] rounded-tl-[10px] w-1/3 flex'"
                    content-classes="px-4 py-12"
                >
                    <template #image>
                        <StoryblokImage
                            v-if="data.featuredArticle.image"
                            :src="data.featuredArticle.image.src"
                            :alt="data.featuredArticle.image.alt"
                            class="overflow-hidden rounded-bl-[10px] rounded-tl-[10px] object-cover"
                            itemprop="image"
                            sizes="200px md:250px lg:340px"
                            :image-height="300"
                        />
                    </template>
                    <template #title>
                        <div class="mb-4 text-sm" itemprop="datePublished">
                            {{ formattedDate(data.featuredArticle.date) }}
                        </div>
                        <div class="mb-2 text-xl font-bold" itemprop="headline">
                            {{ data.featuredArticle.title }}
                        </div>
                    </template>
                    <template #description>
                        <WysiwygContent
                            v-if="data.featuredArticle.description"
                            class="line-clamp-5"
                            :content="data.featuredArticle.shortDescription"
                        />
                    </template>
                </BaseCard>
                <NewsletterCta
                    v-if="data.showCta"
                    class="md:flex md:w-1/3 md:flex-col"
                    :heading="data.ctaHeading"
                    :text="data.ctaText"
                    :submit-label="data.ctaButtonLabel"
                />
            </div>
            <BlogCategoryMenu
                id="blog-category-menu"
                :categories="data.categories"
                :current-category="data.currentCategory"
                class="mb-8"
            />
            <div
                class="grid grid-cols-1 gap-[30px] md:grid-cols-2 xl:grid-cols-4"
            >
                <BaseCard
                    v-for="(article, index) in data.articles"
                    :key="'article-' + index"
                    :card="blogCard(article)"
                    main-classes="flex-col !no-underline"
                >
                    <template #title>
                        <div class="mb-4 text-sm" itemprop="datePublished">
                            {{ formattedDate(article.date) }}
                        </div>
                        <div class="mb-2 text-xl font-bold" itemprop="headline">
                            {{ article.title }}
                        </div>
                    </template>
                </BaseCard>
            </div>
        </div>
    </BlockContainer>
</template>

<script setup lang="ts">
import type { BlogArticleGridType } from '#shared/types/BlogArticleGridType';
import type { CardType } from '#shared/types/ui/CardType';
import BlogCategoryMenu from '~/components/page-building/blog/BlogCategoryMenu.vue';
import NewsletterCta from '~/components/page-building/components/NewsletterCta.vue';
import BaseCard from '~/components/ui/BaseCard.vue';

defineProps<{
    data: BlogArticleGridType;
}>();

const blogCard = (item) => {
    return {
        title: item.title,
        image: item.image,
        tags: item.tags || [],
        link: item.link,
        description: item.shortDescription,
    } as CardType;
};
</script>
